import { derivePersona } from '@core/simulation/persona';
import { newSeed } from './shuffle';

/**
 * Anti-memorisation selection (§14.2, §14.3).
 *
 * Two exclusions, both "not the one you had last time": which variant of a
 * scenario is served, and which persona plays it. Together they are why a
 * memorised path does not survive a second attempt — the same call with a
 * different prospect is a materially different call (§14).
 */

/** §14.3 — one variant at random from the group, excluding the most recent. */
export function pickVariant(pool: string[], mostRecent?: string | null): string | null {
  if (pool.length === 0) return null;
  const remaining = mostRecent ? pool.filter((id) => id !== mostRecent) : pool;
  // With a single authored variant there is nothing to exclude and repeating it
  // beats refusing to serve the stage.
  const from = remaining.length ? remaining : pool;
  return from[Math.floor(Math.random() * from.length)]!;
}

/**
 * §14.2 — a seed whose derived persona is not the rep's most recent one.
 *
 * The exclusion is applied by *choosing the seed*, never by passing the excluded
 * persona into the derivation. The server re-derives the persona from the stored
 * seed alone (§9.7) and has no idea who the rep faced last time; if the client
 * filtered the pool and the server did not, the two would disagree about who was
 * on the call and every replayed score would be wrong.
 *
 * Falls back to an unfiltered seed rather than looping forever — with one
 * eligible persona there is no other answer, and repeating a prospect is a much
 * smaller problem than refusing to start.
 */
export function pickSeed(eligiblePersonas: string[], mostRecentPersona?: string | null): number {
  let seed = newSeed();
  if (!mostRecentPersona || eligiblePersonas.length < 2) return seed;
  for (let i = 0; i < 50; i++) {
    if (derivePersona(seed, eligiblePersonas) !== mostRecentPersona) return seed;
    seed = newSeed();
  }
  return seed;
}
